import React, { useLayoutEffect, useRef, useState } from 'react';
import { inject, observer } from 'mobx-react';
import { Button, Flex, Spinner, Text } from 'core/components';
import { AI_DISCLAIMER_SHORT } from './constants';
import KaizenChatSession from './KaizenChatSession';
import KaizenInput from './KaizenInput';
import KaizenEmptyState from './KaizenEmptyState';
import KaizenToolResultPanel from './KaizenToolResultPanel';
import KaizenSidebar from './KaizenSidebar';
import ResizablePanels from './ResizablePanels';
import { FancyInput, KaizenScrollArea } from './KaizenChatSession.styles';

const CHAT_MIN_WIDTH = 350;
const TOOL_RESULT_PANEL_MIN_WIDTH = 400;

/**
 * Whether the element is too narrow to fit the chat and the tool result panel side by side. Tracks the element's own
 * width, so it accounts for the window size and the sidebar.
 */
const useIsTooNarrowForPanel = (ref) => {
  const [isTooNarrow, setIsTooNarrow] = useState(false);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(([entry]) => {
      setIsTooNarrow(entry.contentRect.width < CHAT_MIN_WIDTH + TOOL_RESULT_PANEL_MIN_WIDTH);
    });
    resizeObserver.observe(element);
    return () => resizeObserver.disconnect();
  }, [ref]);

  return isTooNarrow;
};

const KaizenDesktopLayout = inject('$kaizen')(
  observer(
    ({
      $kaizen,
      isChatLoading,
      showSidebar = true,
      selectedToolResults,
      isScrolledToBottom,
      isExistingChatLoading,
      inputRef,
      scrollAreaRef,
      onScroll,
      onScrollToBottom,
      onSelectToolResult,
      onCloseToolResult,
      onCloseToolResults,
      onCreateJourney,
      onSelectJourneyAfterCreation,
      onSelectJourney,
      onNewChat
    }) => {
      const containsQueries =
        $kaizen.chatSessionCollection?.selected?.id && $kaizen.chatSessionCollection?.selected?.get('messages');
      const selectedJourney = $kaizen.chatSessionCollection?.selected;
      const panelsRef = useRef(null);
      // without room for the side panel, tool results expand in place like they do in the copilot
      const inlineToolResults = useIsTooNarrowForPanel(panelsRef);
      const showToolResultPanel = !inlineToolResults && selectedToolResults?.length > 0;

      return (
        <Flex height="100%" width="100%">
          <Flex ref={panelsRef} flex={1} minWidth={0} height="100%">
            <ResizablePanels
              showRightPanel={showToolResultPanel}
              leftMinWidth={CHAT_MIN_WIDTH}
              rightMinWidth={TOOL_RESULT_PANEL_MIN_WIDTH}
              leftPanel={
                isChatLoading ? (
                  <Flex
                    width="100%"
                    height="calc(100vh - 100px)"
                    alignItems="center"
                    justifyContent="center"
                    bg="decks.background"
                  >
                    <Spinner />
                  </Flex>
                ) : (
                  <Flex flexDirection="column" width="100%" bg="decks.background" height="calc(100vh - 100px)">
                    <KaizenScrollArea ref={scrollAreaRef} onScroll={onScroll}>
                      {containsQueries ? (
                        <KaizenChatSession
                          key={$kaizen.chatSessionCollection?.selected.id}
                          chatSessionModel={$kaizen.chatSessionCollection?.selected}
                          scrollToBottom={onScrollToBottom}
                          isScrolledToBottom={isScrolledToBottom}
                          onSelectToolResult={onSelectToolResult}
                          inlineToolResults={inlineToolResults}
                          isExistingChatLoading={isExistingChatLoading}
                        />
                      ) : (
                        <KaizenEmptyState
                          inputRef={inputRef}
                          selectedJourney={selectedJourney}
                          placeholder="Ask anything about your network"
                          createJourney={onCreateJourney}
                          scrollToBottom={onScrollToBottom}
                          selectJourneyAfterCreation={onSelectJourneyAfterCreation}
                          sessionFeatures={$kaizen.selectedSessionFeatures}
                        />
                      )}
                    </KaizenScrollArea>
                    {containsQueries && (
                      <FancyInput>
                        <KaizenInput
                          scrollToBottom={onScrollToBottom}
                          inputRef={inputRef}
                          key={selectedJourney?.id || 'new-journey-input'}
                          placeholder="Ask any question about your network"
                          chatSessionModel={$kaizen.chatSessionCollection?.selected}
                          createJourney={onCreateJourney}
                          selectJourneyAfterCreation={onSelectJourneyAfterCreation}
                          sessionFeatures={$kaizen.selectedSessionFeatures}
                        />
                        <Flex alignItems="center" gap={2} role="contentinfo">
                          {containsQueries && (
                            <Button icon="arrow-down" onClick={onScrollToBottom} small minimal>
                              <Text muted>Scroll to Bottom</Text>
                            </Button>
                          )}
                          <Text small muted>
                            {AI_DISCLAIMER_SHORT}
                          </Text>
                        </Flex>
                      </FancyInput>
                    )}
                  </Flex>
                )
              }
              rightPanel={
                showToolResultPanel && (
                  <KaizenToolResultPanel
                    tools={selectedToolResults}
                    onCloseToolResult={onCloseToolResult}
                    onClose={onCloseToolResults}
                    onSelectToolResult={onSelectToolResult}
                    sessionModel={selectedJourney}
                  />
                )
              }
            />
          </Flex>
          {showSidebar && <KaizenSidebar onSelectJourney={onSelectJourney} onNewChat={onNewChat} />}
        </Flex>
      );
    }
  )
);

export default KaizenDesktopLayout;
